import { Immutable } from "immer";
import * as t from "io-ts";
import { DateFromISOString } from "io-ts-types/lib/DateFromISOString";

import { WebPageURL } from "./nominals";
import { nullable } from "./utils";

/**
 * Base User
 */
const BaseUser = t.readonly(
    t.type({
        username: t.string,
        first_name: t.string,
        last_name: t.string,
        email: t.string,
    }),
);

/**
 * User Account
 */
export const User = t.readonly(
    t.intersection([
        BaseUser,
        t.type({
            has_password: t.boolean,
            is_csr: t.boolean,
        }),
    ]),
);

export type IUser = t.TypeOf<typeof User>;

/**
 * CSR Assisted User
 */
export const CSRAssistedUser = t.readonly(
    t.intersection([
        BaseUser,
        t.type({
            groups: t.readonlyArray(t.string),
            dashboard_link: WebPageURL,
            is_assisting: t.boolean,
            currently_assisted_by: BaseUser,
            assistance_log: t.readonlyArray(
                t.type({
                    id: t.number,
                    csr: BaseUser,
                    action_time: t.string,
                    message: t.string,
                }),
            ),
        }),
    ]),
);

export type ICSRAssistedUser = t.TypeOf<typeof CSRAssistedUser>;

/**
 * User login response
 */
export const UserLoginResponse = t.readonly(
    t.partial({
        redirect: WebPageURL,
        message: t.string,
    }),
);

export type IUserLoginResponse = t.TypeOf<typeof UserLoginResponse>;

/**
 * Session Keep-Alive response
 */
export const SessionKeepAliveResponse = t.readonly(
    t.type({
        session_lifetime_seconds: t.number,
    }),
);

export type ISessionKeepAliveResponse = t.TypeOf<
    typeof SessionKeepAliveResponse
>;

/**
 * OIDC auth refresh response
 */
export const OIDCTokenRefreshResponse = t.readonly(
    t.type({
        refresh_url: t.string,
    }),
);

export type IOIDCTokenRefreshResponse = t.TypeOf<
    typeof OIDCTokenRefreshResponse
>;

export type IEmailOfferSignupRequest = Immutable<{
    email: string;
    zipcode?: string | null;
    campaign_id?: string | null;
    recaptcha?: string | null;
}>;

const LeadWelcomeVoucherAllocation = t.readonly(
    t.type({
        voucher_code: t.string,
        created_datetime: DateFromISOString,
    }),
);

export const EmailOfferSignupResponse = t.readonly(
    t.intersection([
        t.type({
            email: t.string,
        }),
        t.partial({
            zipcode: nullable(t.string),
            campaign_id: nullable(t.string),
            voucher_allocation: nullable(LeadWelcomeVoucherAllocation),
        }),
    ]),
);

export type IEmailOfferSignupResponse = t.TypeOf<
    typeof EmailOfferSignupResponse
>;
